import { ServerBaseUrlKey, type ServerBaseUrl } from './types';
import { canadaServerConfig } from './configs/canada';
import { cnUatServerConfig } from './configs/cnUat';
import { demoServerConfig } from './configs/demo';
import { devServerConfig } from './configs/dev';
import { developmentServerConfig } from './configs/development';
import { dhlUatServerConfig } from './configs/dhlUat';
import { dockerServerConfig } from './configs/docker';
import { frankfurtServerConfig } from './configs/frankfurt';
import { irelandServerConfig } from './configs/ireland';
import { menaServerConfig } from './configs/mena';
import { mergeRequestPreviewServerConfig } from './configs/mergeRequestPreview';
import { productionServerConfig } from './configs/production';
import { sandboxFallbackServerConfig } from './configs/sandboxFallback';
import { sandboxServerConfig } from './configs/sandbox';
import { saoPauloServerConfig } from './configs/saoPaulo';
import { sesServerConfig } from './configs/ses';
import { singaporeServerConfig } from './configs/singapore';
import { stageServerConfig } from './configs/stage';
import { sydneyServerConfig } from './configs/sydney';
import { tokyoServerConfig } from './configs/tokyo';
import { uatServerConfig } from './configs/uat';
import { ukServerConfig } from './configs/uk';
import { applyLocalServerOverrides } from './local-server-overrides';

export {
    ServerBaseUrlKey,
    ServerCIEnvironment,
    ServerEnvironment,
    type ServerBaseUrl
} from './types';

const targetEnvEndpointMapping: Record<string, string> = {
    demo: 'admin.demo.wisesys.info',
    dev: 'admin.dev.wisesys.info',
    development: 'development',
    local: 'development',
    sandbox: 'sandbox',
    stage: 'admin.stage.wisesys.info',
    staging: 'admin.stage.wisesys.info',
    prod: 'enterprise.wisesystems.io',
    production: 'enterprise.wisesystems.io',
    uat: 'admin.uat.wisesys.info',
    'cn-uat': 'admin.cn-uat.wisesys.info',
    'dhl-uat': 'admin.dhl-uat.wisesys.info',
    ses: 'admin.ses.wisesys.info'
};

const localHostnames = new Set([
    'localhost',
    '127.0.0.1',
    '0.0.0.0',
    'wise-multi-client'
]);

const getEndpointForTargetEnv = (hostname: string): string => {
    if (!localHostnames.has(hostname)) return hostname;

    const targetEnv = process.env.VITE_TARGET_ENV?.trim().toLowerCase();
    if (!targetEnv) return hostname;

    return targetEnvEndpointMapping[targetEnv] || hostname;
};

/**
 * A mapping of Interstellar hostnames to various service server URLs
 */
export const endPointToServerMapping: Record<string, ServerBaseUrl> = {
    'admin.demo.wisesys.info': demoServerConfig,
    'admin.dev.wisesys.info': devServerConfig,
    'admin.sandbox.wisesys.info': sandboxServerConfig,
    'admin.stage.wisesys.info': stageServerConfig,
    'admin.uat.wisesys.info': uatServerConfig,
    'wise-web-multi-client-mrs.wisesys.info': mergeRequestPreviewServerConfig,
    'admin.cn-uat.wisesys.info': cnUatServerConfig,
    'admin.dhl-uat.wisesys.info': dhlUatServerConfig,
    'admin.ses.wisesys.info': sesServerConfig,
    'enterprise.au.wisesystems.io': sydneyServerConfig,
    'enterprise.ca.wisesystems.io': canadaServerConfig,
    'enterprise.ce.wisesystems.io': frankfurtServerConfig,
    'enterprise.ie.wisesystems.io': irelandServerConfig,
    'enterprise.jp.wisesystems.io': tokyoServerConfig,
    'enterprise.sa.wisesystems.io': saoPauloServerConfig,
    'enterprise.sg.wisesystems.io': singaporeServerConfig,
    'enterprise.uk.wisesystems.io': ukServerConfig,
    'enterprise.mena.wisesystems.io': menaServerConfig,
    'enterprise.wisesystems.io': productionServerConfig,
    'wise-multi-client:8033': dockerServerConfig,
    development: applyLocalServerOverrides(developmentServerConfig),
    sandbox: sandboxFallbackServerConfig
};

/**
 * Retrieves the base url for the target server
 *
 * @param {ServerBaseUrlKey} server - the target server key to retrieve from the mapping
 * @returns {string} - Returns the URL or empty string if not found
 */
export const getURL = (server: ServerBaseUrlKey): string => {
    const { hostname } = window.location;
    // `VITE_TARGET_ENV` redirects the banner and environment lookups on a
    // local hostname: the environment key drives the DoorDash integrations
    // base url, and without it a stage-targeted dev server quotes against UAT
    // and the dispatch comparison silently fails locally. API, socket, and
    // driver-cost stay local — the driver-cost gateway's CORS allowlist only
    // admits the deployed origin, so local runs go through the :3010 shim.
    const targetEnvAwareKeys = new Set([
        ServerBaseUrlKey.BANNER_SERVICE,
        ServerBaseUrlKey.ENVIRONMENT
    ]);
    const endPoint = targetEnvAwareKeys.has(server)
        ? getEndpointForTargetEnv(hostname)
        : hostname;
    const isSandbox =
        endPoint.match(/.*\.wisesys\.info/) ||
        endPoint.match(/^wisesystems\.gitlab\.io\/.*/);
    const {
        development: { [server]: devServer },
        sandbox: { [server]: sandboxServer }
    } = endPointToServerMapping;
    const envConfig =
        endPointToServerMapping[
            endPoint as keyof typeof endPointToServerMapping
        ] || {};
    const hasEnvServer = Object.prototype.hasOwnProperty.call(
        envConfig,
        server
    );
    const envServer = envConfig[server];

    if (hasEnvServer) {
        return envServer || '';
    }

    // Return empty string instead of undefined to prevent runtime errors
    return (isSandbox && sandboxServer) || devServer || '';
};
